// DeviceSettings.js
// external libs
import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import Spinner from '../../util/spinner-shim'
import statusCalculate from 'altrac-application-status'
import { FormattedMessage } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import AppDispatcher from '../../dispatchers/AppDispatcher'

// internal libs
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import ConfigureButtons from '../partials/ConfigurePageComponents/ConfigureButtons'
import TelemetryButtons from '../partials/ConfigurePageComponents/TelemetryButtons'
import PhysicalButtons from '../partials/ConfigurePageComponents/PhysicalButtons'
import { TextLink } from '../partials/Navigation/Buttons'
import {
  Default,
} from '../../models/ConfigureModels'

import styles from '../../../css/devicePage.module.css'
import withAppStore from '../../util/withAppStore.jsx'

const DeviceSettings = function DeviceSettings ({ device, data }) {
  const applicationSettings = device.application_settings || { settings: {} }
  const applicationSettingsNew = device.application_settings_new || { settings: {} }
  const physical = device.physical || {}
  const reading0 = device.reading0 || {}
  const interfaceVersioned = device.interface_versioned || {}
  const basePath = `/${device.customer_id}/devices/${device.id}/`
  const settingPath = `${basePath}settings/`
  const alertPath = `${basePath}rule/`

  const {
    interface: intf = {},
    interface_versioned: intfVersioned = {},
    customer_id: customerID,
    id: deviceID,
  } = device || {}

  const deviceType = physical.deviceType || ''

  const intfAdvancedPage = intf ? intf.advancedPage : undefined
  const intfVersionedAdvancedPage = intfVersioned ? intfVersioned.data.advancedPage : undefined

  const advancedPage = intfVersionedAdvancedPage || intfAdvancedPage || Default.advancedPage

  const {
    physicalTypes = [],
  } = advancedPage || {}

  const vitals = advancedPage.telemetryTypes && advancedPage.telemetryTypes.length > 0
    ? (
      <div>
        <div className={styles.sectionName}>
          <FormattedMessage
            id='device.vitals'
            defaultMessage='Device Vitals'
            description='Device Vitals'
            tagName='h2'
          />
        </div>
        <TelemetryButtons
          device={device}
          status={data.deviceStatus}
          path={basePath}
          telemetryTypes={advancedPage.telemetryTypes}
          customer={data.customer.data}
          pinCode={data.pinCode}
          tempConv={data.user?.locale?.tempConv || 'f'}
          level={data.user?.permissions?.level || 0}
        />
      </div>
      )
    : ''

  const configure = advancedPage.settingTypes && advancedPage.settingTypes.length > 0
    ? (
      <div>
        <div className={styles.sectionName}>
          <FormattedMessage
            id='device.deviceSettings'
            defaultMessage='Device Settings'
            description='Device Settings'
            tagName='h2'
          />
        </div>
        <ConfigureButtons
          applicationSettings={applicationSettings}
          applicationSettingsNew={applicationSettingsNew}
          reading0={reading0}
          physical={physical}
          interfaceVersioned={interfaceVersioned}
          customer={data.customer.data}
          pinCode={data.pinCode}
          path={settingPath}
          settingTypes={advancedPage.settingTypes}
        />
      </div>
      )
    : ''

  const settingHistory = advancedPage.settingHistory && advancedPage.settingHistory.length > 0
    ? (
      <div>
        <div className={styles.sectionName}>
          <FormattedMessage
            id='device.settingHistory'
            defaultMessage='Setting History'
            description='Setting History'
            tagName='h2'
          />
        </div>
        <ConfigureButtons
          applicationSettings={applicationSettings}
          applicationSettingsNew={applicationSettingsNew}
          reading0={reading0}
          physical={physical}
          interfaceVersioned={interfaceVersioned}
          customer={data.customer.data}
          pinCode={data.pinCode}
          path={basePath}
          settingTypes={advancedPage.settingTypes}
          customerID={customerID}
          deviceID={deviceID}
        />
      </div>
      )
    : ''

  const allowedRuleDeviceTypes = [
    'windmachine',
    'pump',
    'coldairdrain',
    'pumpfrostwater',
    'moisturesensor',
    'moisture',
    'temperature',
    'bindicator',
  ]
  const rules = allowedRuleDeviceTypes.includes(deviceType.toLowerCase())
    ? (
      <section>
        <div className={styles.sectionName}>
          <FormattedMessage
            id='device.rules'
            defaultMessage='Rules'
            description='Rules'
            tagName='h2'
          />
        </div>
        <FormattedMessage
          id='device.rules.show'
          defaultMessage='Show Rules'
        >
          {(txt) => (
            <TextLink
              text={txt}
              path={`/${customerID}/devices/${deviceID}/rules`}
            />
          )}
        </FormattedMessage>
      </section>
      )
    : ''

  return (
    <div className={styles.pageContainer}>
      {rules}
      <div className={styles.sectionName}>
        <FormattedMessage
          id='device.details'
          defaultMessage='Device Details'
          description='Device Details'
          tagName='h2'
        />
      </div>
      <PhysicalButtons
        device={device}
        path={settingPath}
        physicalTypes={physicalTypes}
      />
      {vitals}
      {settingHistory}
      {configure}

    </div>
  )
}

DeviceSettings.propTypes = {
  device: PropTypes.object,
  data: PropTypes.object,
}

/**
 * Shortcut to call AppDispacher.
 * @param {string} action
 * @param {object} obj containing properties to dispatch.
 */
const dispatch = (action, obj = {}) => AppDispatcher.dispatch({ action, ...obj })

function DeviceAdvancedSettings (props) {
  const { customerID, deviceID } = useParams()
  const navigate = useNavigate()

  const [statusUpdateOnce, setStatusUpdateOnce] = React.useState(true)

  const getNewData = React.useCallback(() => {
    dispatch('get-device', { deviceID })
  }, [deviceID])

  const backToDevice = React.useCallback((deviceType, interfaceV) => {
    let path = `/${customerID}/devices/${deviceID}`
    const {
      data: {
        other: {
          page: interfacePage = '',
        } = {},
      } = {},
    } = interfaceV
    switch (deviceType) {
      case 'moistureSensor':
        path = `/${customerID}/moistureSensor/${deviceID}`
        break
      case 'pump':
        if (interfacePage === 'flow') {
          path = `/${customerID}/flow/${deviceID}`
        } else {
          path = `/${customerID}/pump/${deviceID}`
        }
        break
      case 'flow':
        path = `/${customerID}/flow/${deviceID}`
        break
      case 'valve':
        if (interfacePage === 'flow') {
          path = `/${customerID}/flow/${deviceID}`
        } else {
          path = `/${customerID}/valve/${deviceID}`
        }
        break
      default:
        path = `/${customerID}/devices/${deviceID}`
        break
    }

    navigate(path)
  }, [customerID, deviceID, navigate])

  const toDeviceAdmin = React.useCallback(() => {
    navigate(`/${customerID}/devices/${deviceID}/admin`)
  }, [customerID, deviceID, navigate])

  React.useEffect(() => {
    getNewData()
  }, [getNewData])

  React.useEffect(() => {
    const interval = setInterval(getNewData, props.data?.pollInterval || 5000)
    return () => clearInterval(interval)
  }, [getNewData, props.data?.pollInterval])

  React.useEffect(() => {
    return () => {
      dispatch('unload-device-status')
    }
  }, [])

  React.useEffect(() => {
    const { data: { lookupReady, lookup } = {} } = props || {}
    const device = lookup[deviceID]

    if (lookupReady && device && statusUpdateOnce) {
      setStatusUpdateOnce(false)
      const status = statusCalculate(device)
      dispatch('load-device-status', { status })
    }
  }, [props.data?.lookupReady, props.data?.lookup, deviceID, statusUpdateOnce])

  const {
    auth: {
      logout,
    },
    data,
    data: {
      lookupReady,
      lookup,
    } = {},
    pages: {
      DeviceAdvancedSettings: title,
    },
    route: {
      path,
    } = {},
  } = props

  const device = lookupReady && lookup[deviceID]

  const {
    address_alias: addressAlias,
    physical: {
      deviceNumber,
    } = {},
  } = device || {}

  let rightText = ''
  let rightFunction
  if (props.level >= 20) {
    rightText = 'fa-wrench'
    rightFunction = toDeviceAdmin
  }

  const subject = device
    ? (
      <div className={styles.pageWidth}>
        <div>
          <DeviceSettings
            device={device}
            data={data}
          />
        </div>
      </div>
      )
    : (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
      )

  const name = deviceNumber || addressAlias || ''

  return (
    <div>
      <Helmet><title>{title}</title></Helmet>
      <NavigationBar
        backFunc={() => backToDevice(device.physical.deviceType, device.interface_versioned)}
        page={path}
        name={name}
        logOut={() => logout(navigate)}
        rightControl={{
          icon: rightText,
          func: rightFunction,
        }}
      />
      {subject}
      <Footer />
    </div>
  )
}

DeviceAdvancedSettings.propTypes = {
  data: PropTypes.object,
  pages: PropTypes.object,
  route: PropTypes.object,
  level: PropTypes.number,
  auth: PropTypes.object,
}

export default withAppStore(DeviceAdvancedSettings)
